import * as React from 'react';
import { ButtonDef } from '../form/ButtonDef';
import { broadcast, useChannel } from '../hooks/useChannel';
import { newID } from '../utils/unique';
import { closestKey } from '../utils/dom';
import { Icon } from './icons';
import { Modal } from './Modal';

export type NotificationType = "normal" | "modal";
export type NotificationStyle = "success" | "error";

export interface Notification {
    id?: string;

    type?: NotificationType;
    style?: NotificationStyle;

    title?: string;
	message?: any;
	errors?: any[];
    icon?: any;
    displaySeconds?: number;

    hHide?: any;
    hDestroy?: any;
    isLeaving?: boolean;

    notDismissable?: boolean;

    el?: any;

    // modal props
    buttons?: ButtonDef[];
    onAction?: (action: string) => void;
}

const channelName = "notification";

const NotificationMessages = {
	ADD: "add",
	REMOVE: "remove",
}


const defaultDisplaySeconds = 3;

export function newNotification(notification: Notification) {
    broadcast(channelName, {
		type: NotificationMessages.ADD,
		notification,
	});
}

export function newError(options: {
	title?: string,
	apiResponse?: any,
} = {}) {

	const n = {
		title: options.title,
		style: "error",
		errors: (options.apiResponse && options.apiResponse.errors) ? options.apiResponse.errors : null,
	} as Notification;

	newNotification(n);
}


export function remove(notification: Notification) {
    broadcast(channelName, {
		type: NotificationMessages.REMOVE,
		notification
	});
}



export function Notifications(props: {

}) {

	const items = React.useRef<Notification[]>([]);
	const [,refresh] = React.useState({});

	const [modals, setModals] = React.useState<Notification[]>([]);

	useChannel(channelName, React.useCallback((msg) => {
		if (msg.type === NotificationMessages.ADD) {

			const n = msg.notification as Notification;

			if (!n.id) n.id = newID();

			if (n.type === "normal" || n.type === undefined) {
				if (!n.displaySeconds || n.displaySeconds <= 0) {
					n.displaySeconds = defaultDisplaySeconds;
					if (n.style === 'error') n.displaySeconds *= 2;
				}

				n.isLeaving = false;
				n.hHide = setTimeout(() => {
					animateOut(n);
				}, 1000 * n.displaySeconds);
				
				if (n.errors) {
					var actualMsg = [];
					for(var i=0;i<n.errors.length;i++) {
						actualMsg.push(
							<li key={i}>{n.errors[i].message}</li>
						);
					}
					n.message = actualMsg;
				}

				items.current.push(n);
				refresh({});
				reflow();

				// fade in
				setTimeout(() => {
					const el = document.getElementById(n.id!);
					if (el) el.style.opacity = "1";
				}, 50);

			} else if (n.type === "modal") {
				modals.push(n);
				refresh({});

			}

		} else if (msg.type === NotificationMessages.REMOVE) {			
			const n = msg.notification as Notification;
			const i = items.current.indexOf(n);
			if (i >= 0) items.current.splice(i, 1);

		}
	}, []));
	
	function animateOut(n: Notification) {
		if (n.hHide) {
			clearTimeout(n.hHide);
			n.hHide = null;
		}
		n.isLeaving = true;
		const el = document.getElementById(n.id!);
		if (el) {
			el.style.right = "-" + el.offsetWidth + "px";
			el.style.opacity = "0";    
		}
	
		n.hDestroy = setTimeout(() => {
			removeItem(n);
			const el = document.getElementById(n.id!);
			if (el) {
				el.style.right = "-" + el.offsetWidth + "px";
				el.style.opacity = "0";
			}
			reflow();
		}, 500);
	}

    function removeItem(n: Notification) {
        const i = items.current.indexOf(n);
        if (i >= 0) {
            items.current.splice(i, 1);
            // NOTE: no need to re-render as the notification will be gone
            // just wait until next valid reason to render and this removed item will be
            // permanently gone
        }
    }

    function reflow() {
        var y = 0;
		const gap = 10;
        window.setTimeout(() => {
            for(let item of items.current) {
                if (item.isLeaving) continue;

				const el = item.el ? item.el : document.getElementById(item.id!);
				if (el) {
					if (!item.el) item.el = el;

					el.style.top = y + "px";
	
					y += el.offsetHeight + gap;	
				}
            }
        }, 100);
    }	

	function handleDismiss(e: any) {
        const id = closestKey(e.target, ".dismiss-button");
        const n = items.current.find((i) => i.id === id);
        if (n) {
            animateOut(n);
        }
    }

	const els = [];
	for(let item of items.current) {

		const dismissable = item.notDismissable ?
			null :
			<button
				className={"dismiss-button flat margin-left-5 padding-0"}
				data-key={item.id}
				onClick={handleDismiss}
			><Icon type="cross" size="small" /></button>;

		els.push(
			<div
				className={[
					"item z-top bg-brand-secondary",
					(item.style === "error" ? "bg-error-secondary text-inverse" : ""),
					(item.style === "success" ? "bg-success text-inverse" : ""),
				].join(" ")}
				key={item.id}
				id={item.id}
			>
				<div className="flex horiz">
					{item.icon}
					<div>
						<div className="title">{item.title}</div>
						<div className="message fill">{item.message}</div>
					</div>
					{dismissable}
				</div>
			</div>
		); 
	}

	for(let m of modals) {
		els.push(
			<NotificationModal
				key={"modal-" + m.id}
				notification={m}
				onClose={(n) => {
					const i = modals.indexOf(n);
					if (i >= 0) modals.splice(i, 1);
					refresh({});
				}}
			/>
		)
	}

	return (
		<div
			className="notifications"
			style={{right: "0", top: "0"}}
		>
			{els}
		</div>
	);	
}

function NotificationModal(props: {
	notification: Notification,
	onClose: (notification: Notification) => void,
}) {
	const n = props.notification;

	return (
		<Modal
			title={n.title ? n.title : "Notification"}

			size="width-25vw"
			
			showCancel={false}
			saveTitle="Close"
			onSave={() => props.onClose(props.notification)}
		>
			{n.message}
		</Modal>
	);
}

// export class Notifications extends React.Component<Props, State> {


//     handleModalButtonClick(e: any) {
//         const {
//             modal,
//         } = this.state;

//         if (!modal) return;

//         const action = utils.closestKey(e.target, ".button");
//         modal.onAction(action);

//         // NOTE: any button have to assume has handled whatever is necessary to have the
//         // dismissal of this modal
//         this.setState({ modal: null });
//     }

//     renderModal() {
//         const {
//             modal,
//         } = this.state;

//         if (!modal) return null;

//         const buttons = [];
//         if (modal.buttons) {
//             for(var i=0;i<modal.buttons.length;i++) {
//                 const btn = modal.buttons[i];
//                 buttons.push(
//                     <ui.Button
//                         type={btn.type ? btn.type : ui.ButtonStyle.Primary}
//                         key={btn.action}
//                         data-key={btn.action}
//                         title={btn.title}
//                         onClick={this.handleModalButtonClick}
//                     />
//                 );
//             }
//         }

//         return (
//             <ui.Modal
//                 title={modal.title}
//                 buttons={buttons}
//                 size="small"
//                 showCancel={true}
//                 onCancel={() => {
//                     if (modal.onAction) modal.onAction("cancel");
//                     this.setState({ modal: null });
//                 }}
//             >
//                 {modal.message}
//             </ui.Modal>
//         );
//     }


// }
